import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n/languages';

interface LanguageSelectorProps {
  availableLanguages?: string[];
}

export function LanguageSelector({ availableLanguages }: LanguageSelectorProps) {
  const { i18n } = useTranslation('common');

  const languages = availableLanguages
    ? SUPPORTED_LANGUAGES.filter((lang) => availableLanguages.includes(lang.code))
    : SUPPORTED_LANGUAGES;

  // Auto-switch to the only available language if current language is not available
  useEffect(() => {
    if (availableLanguages && availableLanguages.length > 0) {
      const currentLangAvailable = availableLanguages.includes(i18n.language);
      if (!currentLangAvailable) {
        i18n.changeLanguage(availableLanguages[0]);
      }
    }
  }, [availableLanguages, i18n]);

  // Initialize i18n from persisted language choice on mount
  useEffect(() => {
    const stored = localStorage.getItem('bookinghub_language');
    if (stored && stored !== i18n.language) {
      i18n.changeLanguage(stored);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (languages.length <= 1) {
    return null;
  }

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('bookinghub_language', languageCode);
  };

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const shortCode = currentLanguage.code.split('-')[0].toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Select language"
        className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-1 text-xs font-medium text-foreground/80 transition hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-sm leading-none">{currentLanguage.flag}</span>
        <span className="hidden sm:inline tabular-nums">{shortCode}</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {languages.map((language) => {
          const active = language.code === i18n.language;
          return (
            <DropdownMenuItem
              key={language.code}
              onSelect={() => handleLanguageChange(language.code)}
              className={cn('gap-2 text-sm', active && 'font-medium')}
            >
              <span className="text-base leading-none">{language.flag}</span>
              <span className="flex-1">{language.name}</span>
              {active && <Check className="h-3.5 w-3.5 opacity-70" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
